/** @jsx jsx */
import {
  Button,
  ChevronDownIcon,
  ChevronUpIcon,
  Flex,
  Paragraph,
  ShowMoreHorizontalIcon,
  Tooltip,
} from "@eworkit/iris";
import React, { FC, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { CompositeItem } from "reakit/Composite";
import { DialogDisclosure, useDialogState } from "reakit/Dialog";
import { MenuButton } from "reakit/Menu";
import { jsx } from "theme-ui";

import { DropdownMenu, DropdownMenuItem, useDropdownMenuState } from "@/components/dropdowns";
import { TimeReport, TimeReportAggregateReturn, TimeReportProjectAggregate } from "@/data/time-reports";

import { DeleteTimeReportProjectModal, TimeReportProjectModal } from "../../projects";
import { useTimeReportCalendarGrid } from "./grid/TimeReportCalendarGridContext";
import { TimeReportCalendarRow } from "./grid/TimeReportCalendarRow";
import { TimeReportCalendarRowGroup } from "./grid/TimeReportCalendarRowGroup";
import { TimeReportCalendarRowHeader } from "./grid/TimeReportCalendarRowHeader";
import { TimeReportCalendarPayCodeRow } from "./TimeReportCalendarPayCodeRow";
import { TimeReportCalendarProjectRowGroupActions } from "./TimeReportCalendarProjectRowGroupActions";

interface TimeReportCalendarProjectRowGroupProps {
  timeReport: TimeReport;
  project: TimeReportProjectAggregate;
  aggregateData: TimeReportAggregateReturn;
  editable?: boolean;
}

export const TimeReportCalendarProjectRowGroup: FC<TimeReportCalendarProjectRowGroupProps> = ({
  timeReport,
  project,
  aggregateData,
  editable,
  ...restProps
}) => {
  const { t } = useTranslation("session");

  const [isExpanded, toggleIsExpanded] = useReducer(s => !s, true);

  const { compositeState } = useTimeReportCalendarGrid();

  const actionsState = useDropdownMenuState();
  const editProjectModalState = useDialogState({ animated: true });
  const deleteProjectModalState = useDialogState({ animated: true });

  return (
    <TimeReportCalendarRowGroup {...restProps}>
      <TimeReportCalendarRow>
        <TimeReportCalendarRowHeader
          sx={{
            py: [14, 14, 14, 14],
            border: theme => ["none", null, null, `1px solid ${theme.colors?.n200}`],
          }}>
          <Flex flexDirection={["column", null, null, "row"]} justifyContent="space-between">
            <Tooltip content={project.name}>
              {p => (
                <Paragraph bold ellipsis {...p}>
                  {project.name}
                </Paragraph>
              )}
            </Tooltip>
            <Paragraph bold color={project.loggedTime.isEmpty ? "n500" : "n1000"}>
              {project.loggedTime.formattedAmount}
            </Paragraph>
          </Flex>
        </TimeReportCalendarRowHeader>

        <TimeReportCalendarProjectRowGroupActions columns={aggregateData.dateRangeSize}>
          <Flex gap="xxs">
            {editable && (
              <Button
                as={MenuButton}
                {...actionsState}
                variant="ghost"
                size="sm"
                iconLeft={<ShowMoreHorizontalIcon />}
              />
            )}

            <CompositeItem
              {...compositeState}
              role="button"
              as={Button}
              variant="ghost"
              size="sm"
              onClick={toggleIsExpanded}
              iconLeft={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            />
          </Flex>
        </TimeReportCalendarProjectRowGroupActions>
      </TimeReportCalendarRow>

      {isExpanded && (
        <React.Fragment>
          {project.payCodes.map(payCode => (
            <TimeReportCalendarPayCodeRow key={payCode.sourceGuid} payCode={payCode} />
          ))}
        </React.Fragment>
      )}

      <DropdownMenu {...actionsState}>
        <DropdownMenuItem {...actionsState} as={DialogDisclosure} {...editProjectModalState}>
          {t("TIME_REPORTS.PROJECTS.EDIT_MODAL_HEADER")}
        </DropdownMenuItem>
        <DropdownMenuItem {...actionsState} as={DialogDisclosure} {...deleteProjectModalState}>
          {t("TIME_REPORTS.PROJECTS.DELETE_MODAL_HEADER")}
        </DropdownMenuItem>
      </DropdownMenu>

      <TimeReportProjectModal
        dialogState={editProjectModalState}
        timeReport={timeReport}
        projectToUpdate={project.project}
      />
      <DeleteTimeReportProjectModal
        dialogState={deleteProjectModalState}
        timeReport={timeReport}
        projectGuid={project.guid}
      />
    </TimeReportCalendarRowGroup>
  );
};
